import type { Checkout, CheckoutOption, DataLayerEvent } from '../types';
import type { AddToCartData, CartEntryData } from '../../types';
import { Event } from '../types';
import {
  mapRolexProductDetailsToAnalyticsProduct,
  mapAddToCartDataToAnalyticsProduct,
  mapCartEntriesToAnalyticsProducts,
  mapCartEntryToAnalyticsProducts,
  mapOrderConfirmationToPurchaseEvent,
  mapProductDetailsToAnalyticsProduct,
  mapSelectedVariantToAnalyticsProduct,
  mapPageCategoriesToEpsilonEvent
} from './helpers';

const push = (e: DataLayerEvent): void => {
  dataLayer.push(e);
};

const clearEcommerce = (): void => {
  push({ event: Event.clear, ecommerce: null });
};

const clearAndPush = (e: DataLayerEvent): void => {
  clearEcommerce();
  push(e);
};

export const dataLayerListView = (): void => {
  let viewItemListEvent = dataLayer.find((e) => e.event === 'view_item_list');
  const eventData = {
    event: Event.viewItemList,
    ...mapPageCategoriesToEpsilonEvent()
  };

  // Update existing event or push new one
  if (viewItemListEvent) {
    viewItemListEvent = eventData;
  } else {
    push(eventData);
  }
};

export const dataLayerRolexProductDetailsView = (): void => {
  const product = mapRolexProductDetailsToAnalyticsProduct();
  clearAndPush({
    event: Event.viewItem,
    ecommerce: {
      detail: {
        products: [product]
      }
    }
  });
};

export const dataLayerRolexContactUsFormSubmitted = (): void => {
  push({
    event: Event.submitRolexContactUsForm
  });
};

export const dataLayerRolexContactUsCallButtonClicked = (): void => {
  push({
    event: Event.clickRolexContactUsCallButton
  });
};

export const dataLayerRolexContactUsGetDirectionsButtonClicked = (): void => {
  push({
    event: Event.clickRolexContactUsGetDirectionsButton
  });
};

export const dataLayerProductDetailsView = (): void => {
  const product = mapProductDetailsToAnalyticsProduct();
  clearAndPush({
    event: Event.viewItem,
    ecommerce: {
      detail: {
        products: [product]
      }
    }
  });
};

export const dataLayerProductDetailsViewUpdate = (): void => {
  const { selectedVariant } = window.globalProductDetails;
  const product = selectedVariant && mapSelectedVariantToAnalyticsProduct(selectedVariant);

  clearAndPush({
    event: Event.viewItemUpdate,
    ecommerce: {
      detail: {
        products: [product]
      }
    }
  });
};

export const dataLayerAddToCart = (d: AddToCartData): void => {
  const product = mapAddToCartDataToAnalyticsProduct(d);

  clearAndPush({
    event: Event.addToCart,
    ecommerce: {
      add: {
        products: [product]
      }
    }
  });
};

export const dataLayerIncreaseQuantity = (d: CartEntryData): void => {
  const product = mapCartEntryToAnalyticsProducts(d);
  product.quantity = 1;

  clearAndPush({
    event: Event.addToCart,
    ecommerce: {
      add: {
        products: [product]
      }
    }
  });
};

export const dataLayerDecreaseQuantity = (d: CartEntryData): void => {
  const product = mapCartEntryToAnalyticsProducts(d);
  product.quantity = 1;

  clearAndPush({
    event: Event.removeFromCart,
    ecommerce: {
      remove: {
        products: [product]
      }
    }
  });
};

export const dataLayerRemoveFromCart = (d: CartEntryData): void => {
  const product = mapCartEntryToAnalyticsProducts(d);
  clearAndPush({
    event: Event.removeFromCart,
    ecommerce: {
      remove: {
        products: [product]
      }
    }
  });
};

export const dataLayerCheckout = ({ cartEntries, step, option }: Checkout): void => {
  const actionField = option ? { step, option } : { step };
  const products = cartEntries && mapCartEntriesToAnalyticsProducts(cartEntries);
  clearAndPush({
    event: Event.checkout,
    ecommerce: {
      checkout: { actionField, products }
    }
  });
};

export const dataLayerCheckoutOption = ({ step, option }: CheckoutOption): void => {
  push({
    event: Event.checkoutOption,
    ecommerce: {
      checkout_option: {
        actionField: { step, option }
      }
    }
  });
};

export const dataLayerPurchase = (): void => {
  const purchase = mapOrderConfirmationToPurchaseEvent();

  clearAndPush({
    event: Event.purchase,
    ...purchase
  });
};
